import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {CardEntryProvider, PaymentType, SavedCardType} from "../../types";
import ServiceClient from "../../utils/ServiceClient";
import {type CardEntryValidationError, useCardEntryForm} from "../../hooks/useCardEntryForm";
import {useCardTokenization} from "../../hooks/useCardTokenization";
import {
  buildSafeCardTokenizationErrorLog, cardTokenizationPreparationErrorMessage, isCardTokenizationPreparationError,
  isCardTokenizationSupported
} from "../../services/cardTokenization";
import {isFinixConfigured} from "../../services/finixTokenization";
import {isPaysafeConfigured} from "../../services/paysafeTokenization";
import {CardDestinationView, DestinationId} from "./types";
import {
  buildCardDestinationView, buildFinixDestinationView, buildPaysafeDestinationView, buildSafeWithdrawFlowErrorLog,
  isRequestTimeout
} from "./utils";

type Params = {
  amount: string, userId: string, methodType?: PaymentType, isDestinationStepActive: boolean,
  cardEntryProvider?: CardEntryProvider
};

type SaveCardOperation = { userId: string, amount: string, methodType?: PaymentType, isAddingCard: boolean };

const saveCardErrorMessage = 'We could not save this card. Please check the details and try again.';
const saveCardTimeoutMessage = 'We could not confirm whether this card was saved. Check your saved cards before trying again.';
const savedCardsLoadErrorMessage = 'We could not load your saved cards. Please try again.';
const saveCardValidationMessages: Record<CardEntryValidationError, string> = {
  CARD_NUMBER_REQUIRED: 'Enter a valid card number.', CARD_NUMBER_INVALID: 'Enter a valid card number.',
  EXPIRY_REQUIRED: 'Enter a valid expiry date.', EXPIRY_INVALID: 'Enter a valid expiry date.',
  CVC_REQUIRED: 'Enter a valid CVC.', CVC_INVALID: 'Enter a valid CVC.'
};

/**
 * The card the user already saved with the server-chosen provider (SMG-4057), as a destination, so
 * a returning user is not made to enter it again. Best effort: on any failure the list simply lacks
 * it and "Add card" remains the way in — it never fails the saved-cards load.
 */
const loadSavedPaysafeDestination = async (): Promise<CardDestinationView | undefined> => {
  try {
    const instrument = await ServiceClient.getPaysafeInstrument();
    if (!instrument?.provisioned || !instrument.destinationRef) return undefined;
    return buildPaysafeDestinationView(instrument.destinationRef, instrument.lastFour);
  } catch (err) {
    console.log('saved payout card lookup error', buildSafeWithdrawFlowErrorLog(err));
    return undefined;
  }
};

/**
 * The same for Finix (SMG-4797): the user's one Finix card, so reopening the withdraw flow still
 * offers it. Offered only as a card Finix can push to — a bank account is not a card destination, and
 * an INELIGIBLE card is the one the save itself refuses to register. Best effort, as above.
 */
const loadSavedFinixDestination = async (): Promise<CardDestinationView | undefined> => {
  try {
    const instrument = await ServiceClient.getFinixInstrument();
    if (!instrument?.provisioned || !instrument.destinationRef || instrument.instrumentType !== 'CARD'
      || instrument.eligibility === 'INELIGIBLE') return undefined;
    return buildFinixDestinationView(instrument.destinationRef, instrument.lastFour);
  } catch (err) {
    console.log('saved payout card lookup error', buildSafeWithdrawFlowErrorLog(err));
    return undefined;
  }
};

/**
 * The saved card of whichever provider new cards are entered with — exactly WithdrawFlowModal's choice:
 * Paysafe when the server names it AND this build carries the Paysafe key, otherwise Finix when this
 * build carries Finix. Neither → none. Keyed on the form actually shown, so a card saved through the
 * Finix form (a build without the Paysafe key) is the one found again.
 */
const loadSavedProviderDestination = (cardEntryProvider?: CardEntryProvider) => {
  if (cardEntryProvider === 'PAYSAFE' && isPaysafeConfigured()) return loadSavedPaysafeDestination();
  return isFinixConfigured() ? loadSavedFinixDestination() : undefined;
};

export const useSavedCardManagement = ({
  amount, userId, methodType, isDestinationStepActive, cardEntryProvider
}: Params) => {
  const canAddCard = isCardTokenizationSupported();
  const [savedCards, setSavedCards] = useState<SavedCardType[]>([]);
  const [isLoadingSavedCards, setIsLoadingSavedCards] = useState(false);
  const [hasLoadedSavedCards, setHasLoadedSavedCards] = useState(false);
  const [savedCardsError, setSavedCardsError] = useState('');
  const [destinationId, setDestinationId] = useState<DestinationId>('');
  // A Finix (SMG-4557) or Paysafe (SMG-4057) instrument is not a SavedCard, so getSavedCards() will
  // never return it. It is registered by the save that created it, or found by the provider's
  // saved-card lookup (SMG-4797 added Finix's), and lives alongside the loaded list. One slot serves
  // both — the server picks one card-entry provider per flow — so a new card replaces the old one,
  // as it does on the server.
  const [providerDestination, setProviderDestination] = useState<CardDestinationView>();
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [saveCardFormError, setSaveCardFormError] = useState('');
  const [isSavingCard, setIsSavingCard] = useState(false);
  const [requiresSavedCardsCheck, setRequiresSavedCardsCheck] = useState(false);
  const {
    values: cardEntryValues, cardNumberInputProps, expiryInputProps, cvcInputProps, reset: resetCardEntryForm,
    clearSensitiveFields, validate: validateCardEntry
  } = useCardEntryForm({onChange: () => setSaveCardFormError('')});
  const {
    status: tokenizationStatus, errorMessage: tokenizationError, retry: retryTokenization, tokenizeCard
  } = useCardTokenization(!!amount && isAddingCard && canAddCard);
  const savedCardsRequestIdRef = useRef(0);
  const activeSaveCardOperationRef = useRef<SaveCardOperation>();
  const latestSaveCardContextRef = useRef<SaveCardOperation>({
    userId: '', amount, methodType: undefined, isAddingCard: false
  });
  latestSaveCardContextRef.current = {userId, amount, methodType, isAddingCard};

  const cardDestinations = useMemo(() => {
    const loaded = savedCards.map(buildCardDestinationView);
    return providerDestination ? [providerDestination, ...loaded] : loaded;
  }, [providerDestination, savedCards]);
  const selectedDestination = methodType === 'CARD' ? cardDestinations.find(destination => destination.id === destinationId) || cardDestinations[0] : undefined;
  const canContinueFromDestinationStep = methodType === 'CARD' && !isLoadingSavedCards && !savedCardsError && !!selectedDestination;
  const resetSaveCardForm = useCallback(() => {
    resetCardEntryForm();
    setSaveCardFormError('');
    setRequiresSavedCardsCheck(false);
  }, [resetCardEntryForm]);

  const cancelSavedCardsLoad = useCallback(() => {
    savedCardsRequestIdRef.current += 1;
    setIsLoadingSavedCards(false);
  }, []);

  const resetSavedCardFlow = useCallback(() => {
    activeSaveCardOperationRef.current = undefined;
    cancelSavedCardsLoad();
    setSavedCards([]);
    setSavedCardsError('');
    setHasLoadedSavedCards(false);
    setDestinationId('');
    setProviderDestination(undefined);
    setIsAddingCard(false);
    setIsSavingCard(false);
    setRequiresSavedCardsCheck(false);
    resetSaveCardForm();
  }, [cancelSavedCardsLoad, resetSaveCardForm]);

  const loadSavedCards = useCallback(() => {
    if (methodType !== 'CARD') return;

    const requestId = ++savedCardsRequestIdRef.current;
    setIsLoadingSavedCards(true);
    setSavedCardsError('');

    // Only a hosted-field provider (Paysafe, or Finix) has a saved card of its own to look up; with
    // neither this is the getSavedCards() load alone, exactly as before.
    ServiceClient.getPayGateToken().then(() => Promise.all([
      ServiceClient.getSavedCards(), loadSavedProviderDestination(cardEntryProvider)
    ])).then(([cards, savedProviderDestination]) => {
      if (requestId !== savedCardsRequestIdRef.current) return;

      const nextCards = cards || [];
      // The provider's card leads the list (as it does once registered), so it is the default pick.
      const nextIds = [
        ...(savedProviderDestination ? [savedProviderDestination.id] : []), ...nextCards.map(card => card.cardUid)
      ];
      const nextDestinationId = nextIds.includes(destinationId) ? destinationId : nextIds[0] || '';
      if (savedProviderDestination) setProviderDestination(savedProviderDestination);
      setSavedCards(nextCards);
      setHasLoadedSavedCards(true);
      setDestinationId(nextDestinationId);

    }).catch(err => {
      if (requestId !== savedCardsRequestIdRef.current) return;

      console.log('getSavedCards error', buildSafeWithdrawFlowErrorLog(err));
      setSavedCards([]);
      setDestinationId('');
      setHasLoadedSavedCards(true);
      setSavedCardsError(savedCardsLoadErrorMessage);

    }).finally(() => {
      if (requestId !== savedCardsRequestIdRef.current) return;
      setIsLoadingSavedCards(false);
    });
  }, [cardEntryProvider, destinationId, methodType]);

  useEffect(() => () => {
    savedCardsRequestIdRef.current += 1;
    activeSaveCardOperationRef.current = undefined;
  }, []);

  useEffect(() => {
    if (!isDestinationStepActive || methodType !== 'CARD' || hasLoadedSavedCards || isLoadingSavedCards) return;
    loadSavedCards();
  }, [hasLoadedSavedCards, isDestinationStepActive, isLoadingSavedCards, loadSavedCards, methodType]);

  /**
   * Make a just-saved provider-bound instrument (Finix or Paysafe) the selected destination.
   *
   * <p>Selecting it is the point, not a convenience: without this the user is returned to a list
   * that still reads "No saved payout cards yet", Continue stays disabled, and the only affordance
   * offered is to add another card — which mints a second instrument.</p>
   */
  const registerProviderDestination = useCallback((view: CardDestinationView) => {
    setProviderDestination(view);
    setDestinationId(view.id);
    setHasLoadedSavedCards(true);
    setSavedCardsError('');
    setIsAddingCard(false);
  }, []);

  const registerFinixDestination = useCallback((destinationRef: string, lastFour?: string) => {
    registerProviderDestination(buildFinixDestinationView(destinationRef, lastFour));
  }, [registerProviderDestination]);

  const registerPaysafeDestination = useCallback((destinationRef: string, lastFour?: string) => {
    registerProviderDestination(buildPaysafeDestinationView(destinationRef, lastFour));
  }, [registerProviderDestination]);

  const startAddCard = useCallback(() => {
    if (!canAddCard) return;

    setSaveCardFormError('');
    setRequiresSavedCardsCheck(false);
    setIsAddingCard(true);
  }, [canAddCard]);

  const checkSavedCardsAfterUnknownSave = useCallback(() => {
    if (isSavingCard) return;

    resetSaveCardForm();
    setIsAddingCard(false);
    setHasLoadedSavedCards(false);
    setSavedCardsError('');
    loadSavedCards();
  }, [isSavingCard, loadSavedCards, resetSaveCardForm]);

  const cancelAddCard = useCallback(() => {
    if (isSavingCard) return;
    resetSaveCardForm();
    setIsAddingCard(false);
  }, [isSavingCard, resetSaveCardForm]);

  const submitSaveCard = useCallback(async () => {
    if (activeSaveCardOperationRef.current || isSavingCard || requiresSavedCardsCheck || methodType !== 'CARD') return;

    if (tokenizationStatus !== 'ready') {
      setSaveCardFormError(tokenizationError || cardTokenizationPreparationErrorMessage);
      return;
    }

    const validationError = validateCardEntry();
    if (validationError) {
      setSaveCardFormError(saveCardValidationMessages[validationError]);
      return;
    }

    const operation = latestSaveCardContextRef.current;
    const isCurrentOperation = () => {
      const latestContext = latestSaveCardContextRef.current;
      return activeSaveCardOperationRef.current === operation && latestContext.userId === operation.userId && latestContext.amount === operation.amount && latestContext.methodType === operation.methodType && latestContext.isAddingCard === operation.isAddingCard;
    };

    activeSaveCardOperationRef.current = operation;
    setIsSavingCard(true);
    setSaveCardFormError('');
    let saveCardRequestStarted = false;

    try {
      const {number, cvc, expiry} = cardEntryValues;
      const eProtectResponse = await tokenizeCard(number, cvc).finally(clearSensitiveFields);
      if (!isCurrentOperation()) return;

      const payGateToken = await ServiceClient.getPayGateToken();
      if (!isCurrentOperation()) return;
      saveCardRequestStarted = true;
      const savedCard = await ServiceClient.saveCard({
        payPageRegistrationId: eProtectResponse.paypageRegistrationId, expirationDate: expiry,
        cardType: eProtectResponse.type || null
      }, payGateToken);
      if (!isCurrentOperation()) return;

      setSavedCards(currentCards => [...currentCards.filter(card => card.cardUid !== savedCard.cardUid), savedCard]);
      setHasLoadedSavedCards(true);
      setDestinationId(savedCard.cardUid);
      resetSaveCardForm();
      setIsAddingCard(false);

    } catch (err) {
      if (isCardTokenizationPreparationError(err)) {
        console.warn('withdraw card tokenization preparation error', buildSafeCardTokenizationErrorLog(err));
        if (isCurrentOperation()) setSaveCardFormError(cardTokenizationPreparationErrorMessage);

      } else {
        console.log('saveCard error', buildSafeWithdrawFlowErrorLog(err));
        if (isCurrentOperation()) {
          if (saveCardRequestStarted && isRequestTimeout(err)) {
            setSaveCardFormError(saveCardTimeoutMessage);
            setRequiresSavedCardsCheck(true);

          } else {
            setSaveCardFormError(saveCardErrorMessage);
          }
        }
      }

    } finally {
      if (activeSaveCardOperationRef.current === operation) {
        activeSaveCardOperationRef.current = undefined;
        setIsSavingCard(false);
      }
    }
  }, [
    cardEntryValues, clearSensitiveFields, isSavingCard, methodType, requiresSavedCardsCheck, resetSaveCardForm,
    tokenizeCard, tokenizationError, tokenizationStatus, validateCardEntry
  ]);

  const retryCardTokenization = useCallback(() => {
    setSaveCardFormError('');
    retryTokenization();
  }, [retryTokenization]);

  const handleMethodSelected = useCallback((nextMethodType: PaymentType) => {
    if (nextMethodType !== 'CARD') cancelSavedCardsLoad();
    setIsAddingCard(false);
    setSaveCardFormError('');
  }, [cancelSavedCardsLoad]);

  const selectDestination = useCallback((nextDestinationId: DestinationId) => {
    if (methodType !== 'CARD') return;
    setDestinationId(nextDestinationId);
  }, [methodType]);

  return {
    cardDestinations, selectedDestination, destinationId, canAddCard, canContinueFromDestinationStep,
    isLoadingSavedCards, hasLoadedSavedCards, savedCardsError, isAddingCard, saveCardFormError, isSavingCard,
    requiresSavedCardsCheck, tokenizationStatus, tokenizationError, cardNumberInputProps, expiryInputProps,
    cvcInputProps, cancelSavedCardsLoad, resetSavedCardFlow, loadSavedCards, startAddCard, registerFinixDestination,
    registerPaysafeDestination, cancelAddCard, checkSavedCardsAfterUnknownSave, submitSaveCard, retryCardTokenization,
    handleMethodSelected, selectDestination
  };
};
